// MACHINERY V2 (Stroje) — V4's kiosk header below 1280 (M2.16b).
//
// Rendered truth: mobile/screenshots/12-v4-phone.png (stacked: eyebrow, plant
// name, then the counter row with the clock at its right) and
// 13-v4-tablet-1024.png (one bar: eyebrow + plant left, counters and clock
// right, on a card background with a rule under it). Both viewed before this
// file was written. Reference impl: mobile/machinery-mobile.jsx MbV4Phone
// (:414-431) and MbV4Tablet (:440-455).
//
// >>> THE X STAYS, AND ITS ABSENCE FROM BOARD 12 IS NOT A SPEC. <<< The mobile
// package was exported BEFORE the owner ruling of 2026-08-24 that put a
// persistent exit X on the wall "just like we have in the Settings menu"
// (WallExitButton.tsx's header). Shipping the drawing literally would delete a
// control the owner explicitly asked for, and on a PHONE it is worse than on a
// laptop: `fleetView` is localStorage-persisted (M2.13 ruling 3), there is no
// toolbar toggle below 1280, and a phone has no Esc key — so without the X the
// only way out of the wall would be the filter sheet's ZOBRAZENIE section, three
// taps away and on a surface the kiosk does not render. >>> DO NOT remove the X
// to match the PNG. <<<
//
// WHY IT IS NOT WallHeader WITH SMALLER NUMBERS. That header is built around a
// 1920 bar: an 18/800 wordmark, two dividers, 86px of reserved right padding for
// the pinned X, and a `flexWrap` whose spacer only exists on line 1. At 390 the
// wordmark is redundant (the phone chrome above already carries it), the
// dividers have nothing to divide, and the X wants a corner rather than a
// reserved column. The COUNTERS are shared — WallCounters with `size`, one
// counter vocabulary at three scales.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import WallExitButton from "./WallExitButton";
import WallLiveClock from "./WallLiveClock";
import WallCounters from "./wallHeaderCounters";

export interface WallMobileHeaderProps {
  plant: string;
  /** Already-translated "+N more", or null — see WallHeader's note on why this
   *  is a separate field and never glued onto `plant` with a " + ". */
  plantMore: string | null;
  counts: Record<string, number>;
  lastTelemetryOkAt: number;
  /** FleetPage's `exitWall` — the same callback Esc runs. */
  onExit: () => void;
  /** Tablet lays the eyebrow/plant block and the counters on ONE line, on a
   *  card with a rule under it (board 13); the phone stacks them on the page
   *  background (board 12). */
  tablet: boolean;
}

const WallMobileHeader: React.FC<WallMobileHeaderProps> = ({
  plant, plantMore, counts, lastTelemetryOkAt, onExit, tablet,
}) => {
  const { translate } = useTranslation("myMachinery");

  const title = (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontFamily: "var(--font-mono)", fontSize: tablet ? 10 : 9.5,
          color: "var(--mod-accent)", letterSpacing: "0.14em", fontWeight: 700,
          textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {translate("myMachinery:v2.fleet.wall.eyebrow")}
      </div>
      <div
        style={{
          fontSize: tablet ? 19 : 18, fontWeight: 700, color: "var(--ink)",
          marginTop: 2, whiteSpace: "nowrap", overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {plant}
        {plantMore && (
          <span
            style={{
              fontFamily: "var(--font-mono)", fontSize: 11.5, fontWeight: 500,
              color: "var(--ink-2)", opacity: 0.7, marginLeft: 6,
            }}
          >
            {plantMore}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <header
      style={{
        display: "flex",
        // Tablet: one bar. Phone: title over counters.
        flexDirection: tablet ? "row" : "column",
        alignItems: tablet ? "center" : "stretch",
        gap: tablet ? 16 : 8,
        // The X is the LAST flex child here, not a pinned one — unlike
        // WallHeader, neither layout wraps its own children (the counter row
        // wraps INSIDE WallCounters), so the corner cannot escape.
        padding: tablet ? "12px 16px" : "12px 14px 10px",
        background: tablet ? "var(--card)" : "transparent",
        borderBottom: tablet ? "1px solid var(--border)" : "none",
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      {tablet ? (
        <>
          {title}
          <span style={{ flex: 1, minWidth: 0 }} />
          <WallCounters counts={counts} size="tablet" />
          <WallLiveClock lastTelemetryOkAt={lastTelemetryOkAt} />
          <WallExitButton onExit={onExit} />
        </>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
            {title}
            <span style={{ flex: 1, minWidth: 0 }} />
            <WallExitButton onExit={onExit} />
          </div>
          <div
            style={{
              display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <WallCounters counts={counts} size="phone" />
            <span style={{ flex: 1, minWidth: 0 }} />
            <WallLiveClock lastTelemetryOkAt={lastTelemetryOkAt} />
          </div>
        </>
      )}
    </header>
  );
};

export default WallMobileHeader;
